/** 탭 간 note 문서 브릿지 공통 헬퍼 (시간표 스냅샷과 동일한 LS+BC 패턴) */

export const NOTE_TAB_BRIDGE_GRACE_MS = 2500;
export const NOTE_TAB_BRIDGE_POLL_MS = 200;

/** 메인 프레젠테이션 전용 브라우저 탭 (`/dashboard-presentation`) */
export function isMainPresentationBrowserTab(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.includes('dashboard-presentation');
}

export function postBroadcastChannel(channelName: string, message: unknown): void {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(channelName);
    ch.postMessage(message);
    ch.close();
  } catch {
    /* ignore */
  }
}

export function subscribeBroadcastChannel<T>(
  channelName: string,
  onMessage: (data: T) => void,
  isValid: (data: unknown) => data is T
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  let ch: BroadcastChannel | null = null;
  try {
    ch = new BroadcastChannel(channelName);
    ch.onmessage = (ev: MessageEvent) => {
      if (isValid(ev.data)) onMessage(ev.data);
    };
  } catch {
    ch = null;
  }

  return () => {
    try {
      ch?.close();
    } catch {
      /* ignore */
    }
  };
}

/** LS hit 될 때까지 폴링. onRequest는 시작 시·600ms 후 한 번 더 호출. */
export function waitForCacheHit(options: {
  tryHit: () => boolean;
  onRequest?: () => void;
  graceMs?: number;
  pollMs?: number;
}): Promise<boolean> {
  const graceMs = options.graceMs ?? NOTE_TAB_BRIDGE_GRACE_MS;
  const pollMs = options.pollMs ?? NOTE_TAB_BRIDGE_POLL_MS;

  if (typeof window === 'undefined') return Promise.resolve(false);
  if (options.tryHit()) return Promise.resolve(true);

  options.onRequest?.();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      window.clearInterval(pollId);
      window.clearTimeout(retryId);
      window.clearTimeout(graceId);
      resolve(ok);
    };

    const retryId = window.setTimeout(() => {
      if (!settled) options.onRequest?.();
    }, 600);

    let ticks = 0;
    const maxTicks = Math.ceil(graceMs / pollMs);
    const pollId = window.setInterval(() => {
      ticks += 1;
      if (options.tryHit()) {
        finish(true);
        return;
      }
      if (ticks >= maxTicks) finish(false);
    }, pollMs);

    const graceId = window.setTimeout(() => finish(options.tryHit()), graceMs + 50);
  });
}

export function isIsoNewer(a: string | undefined, b: string | undefined): boolean {
  const ta = a ? Date.parse(a) : NaN;
  const tb = b ? Date.parse(b) : NaN;
  if (!Number.isFinite(ta)) return false;
  if (!Number.isFinite(tb)) return true;
  return ta > tb;
}
